import { Pencil } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import TaxpeerEditorComponent from "./editor/taxpeerEditor.component";

interface EditableTextProps {
	value: string;
	placeholder?: string;
	className?: string;
	maxLength?: number;
	disabled?: boolean;
	/**
	 * When true, renders a multi-line textarea with Shift+Enter for new lines
	 * and Enter to commit. When false, renders a single-line input.
	 */
	multiline?: boolean;
	onCommit: (value: string) => void;
	useTaxpeerEditor?: boolean;
	/**
	 * Stable unique id for TinyMCE instance; required for proper inline mounting.
	 */
	editorId?: string;
}

/**
 * EditableText
 *
 * This component is used to edit a text value.
 */
export default function EditableText({
	value,
	placeholder = "Titel eingeben…",
	className = "",
	maxLength,
	disabled = false,
	multiline = true,
	onCommit,
	useTaxpeerEditor = false,
	editorId,
}: EditableTextProps) {
	const [internal, setInternal] = useState<string>(value ?? "");
	const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
	// Generate a stable, unique id per component instance when using the TinyMCE editor
	const stableEditorIdRef = useRef<string>(
		editorId ||
			(typeof crypto !== "undefined" && "randomUUID" in crypto
				? (crypto as Crypto).randomUUID()
				: `${Date.now()}-${Math.random().toString(36).slice(2)}`),
	);

	useEffect(() => {
		setInternal(value ?? "");
		// After value changes, resize once the DOM reflects the new content (multiline only)
		if (multiline) {
			requestAnimationFrame(() => {
				const el = inputRef.current as HTMLTextAreaElement | null;
				if (!el || !(el instanceof HTMLTextAreaElement)) return;
				el.style.height = "auto";
				el.style.height = `${el.scrollHeight}px`;
			});
		}
	}, [value, multiline]);

	// Debounced autosave for TinyMCE mode to ensure commits even if blur isn't fired
	useEffect(() => {
		if (!useTaxpeerEditor) return;
		const handle = setTimeout(() => {
			const trimmed = internal.trim();
			if (trimmed !== value) onCommit(trimmed);
		}, 800);
		return () => clearTimeout(handle);
	}, [internal, useTaxpeerEditor, value, onCommit]);

	function commit() {
		const trimmed = internal.trim();
		if (trimmed !== value) onCommit(trimmed);
	}

	return (
		<div className="flex flex-row-reverse items-center w-full">
			{useTaxpeerEditor ? (
				<TaxpeerEditorComponent
					id={stableEditorIdRef.current}
					value={internal}
					onChange={(value) => setInternal(value)}
					onBlur={(content) => {
						const next = (content ?? internal).trim();
						if (next !== value) onCommit(next);
					}}
					placeholder={placeholder}
					readOnly={disabled}
					toolbar="author_mode"
					showDefaultTableBorders={false}
				/>
			) : multiline ? (
				<textarea
					ref={inputRef as React.RefObject<HTMLTextAreaElement>}
					value={internal}
					onChange={(e) => {
						setInternal(e.target.value);
						// Auto-resize to fit content
						const el = inputRef.current as HTMLTextAreaElement | null;
						if (el && el instanceof HTMLTextAreaElement) {
							el.style.height = "auto";
							el.style.height = `${el.scrollHeight}px`;
						}
					}}
					onBlur={commit}
					onKeyDown={(e) => {
						if (e.key === "Enter" && !e.shiftKey) {
							e.preventDefault();
							(e.currentTarget as HTMLTextAreaElement).blur();
						}
					}}
					placeholder={placeholder}
					disabled={disabled}
					maxLength={maxLength}
					rows={1}
					className={`peer bg-transparent border border-transparent outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 overflow-hidden ${className}`}
					aria-label={placeholder}
				/>
			) : (
				<input
					ref={inputRef as React.RefObject<HTMLInputElement>}
					type="text"
					value={internal}
					onChange={(e) => setInternal(e.target.value)}
					onBlur={commit}
					onKeyDown={(e) => {
						if (e.key === "Enter") {
							e.preventDefault();
							(e.currentTarget as HTMLInputElement).blur();
						}
					}}
					placeholder={placeholder}
					disabled={disabled}
					maxLength={maxLength}
					className={`peer bg-transparent border border-transparent outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 ${className}`}
					aria-label={placeholder}
				/>
			)}
			<Pencil className="size-3 mr-1 opacity-0 transition-opacity duration-150 peer-hover:opacity-100" />
		</div>
	);
}
